import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Modal, Space, Table, Typography, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import {
  batchCreateDriversMutation,
  getDriversQueryKey,
} from "../../../services/client/@tanstack/react-query.gen";
import { useNotify } from "../../util-components/Notifications/notifications";
import { extractErrorMessage } from "../../../util/extractErrorMessage";
import { parseDriverCsv, type ParsedDriverCsv } from "./driverCsv";

const { Paragraph, Text } = Typography;

type DriverCsvUploadModalProps = {
  open: boolean;
  onClose: () => void;
  site_id: string;
  partner_id: string;
};

export const DriverCsvUploadModal: React.FC<DriverCsvUploadModalProps> = ({
  open,
  onClose,
  site_id,
  partner_id,
}) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [fileName, setFileName] = useState<string>();
  const [parsed, setParsed] = useState<ParsedDriverCsv>();

  const drivers = parsed?.drivers ?? [];

  const handleClose = () => {
    setFileName(undefined);
    setParsed(undefined);
    batchMutation.reset();
    onClose();
  };

  const batchMutation = useMutation({
    ...batchCreateDriversMutation(),
    onSuccess: (ids) => {
      queryClient.invalidateQueries({
        queryKey: getDriversQueryKey({ query: { site_ids: [site_id] } }),
      });
      notify({
        type: "success",
        message: `Created ${ids.length} driver${ids.length === 1 ? "" : "s"}`,
      });
      handleClose();
    },
  });

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setFileName(file.name);
      setParsed(parseDriverCsv((e.target?.result as string) ?? ""));
      batchMutation.reset();
    };
    reader.readAsText(file);
    // Prevent antd from uploading the file itself.
    return false;
  };

  const handleSubmit = () => {
    batchMutation.mutate({
      body: {
        drivers: drivers.map((driver) => ({
          email: driver.email,
          badge_ids: driver.badge_ids,
          partner_id,
          site_ids: [site_id],
        })),
      },
    });
  };

  return (
    <Modal
      title="Upload drivers from CSV"
      open={open}
      onCancel={handleClose}
      width={640}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          disabled={drivers.length === 0}
          loading={batchMutation.isPending}
          onClick={handleSubmit}
        >
          {`Create ${drivers.length} driver${drivers.length === 1 ? "" : "s"}`}
        </Button>,
      ]}
    >
      <Paragraph>
        Columns: <Text code>email</Text> (required) and{" "}
        <Text code>badge_ids</Text> (optional, semicolon-separated). All drivers
        are linked to site <Text code>{site_id}</Text>.
      </Paragraph>
      <Paragraph>
        <Text code>
          email,badge_ids
          <br />
          jane@example.com,badge-001;badge-002
        </Text>
      </Paragraph>

      <Space direction="vertical" className="w-full">
        <Upload
          accept=".csv,text/csv"
          beforeUpload={handleFile}
          showUploadList={false}
          maxCount={1}
        >
          <Button icon={<UploadOutlined />}>
            {fileName ? `Replace ${fileName}` : "Select CSV file"}
          </Button>
        </Upload>

        {batchMutation.isError && (
          <Alert
            type="error"
            showIcon
            message="Upload failed, no drivers were created"
            description={extractErrorMessage(batchMutation.error)}
          />
        )}

        {parsed && !parsed.hasEmailColumn && (
          <Alert
            type="error"
            showIcon
            message="No 'email' column found in the CSV header."
          />
        )}

        {parsed?.hasEmailColumn && drivers.length === 0 && (
          <Alert type="error" showIcon message="No valid drivers found." />
        )}

        {drivers.length > 0 && !parsed?.hasBadgeColumn && (
          <Alert
            type="warning"
            showIcon
            message="No 'badge_ids' column found. Drivers will be created without badges."
          />
        )}

        {parsed && parsed.skippedRows.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`Skipped ${parsed.skippedRows.length} row${parsed.skippedRows.length === 1 ? "" : "s"}`}
            description={
              <ul className="m-0 pl-4">
                {parsed.skippedRows.map((row) => (
                  <li key={row.line}>{`Line ${row.line}: ${row.reason}`}</li>
                ))}
              </ul>
            }
          />
        )}

        {drivers.length > 0 && (
          <Table
            size="small"
            rowKey="email"
            dataSource={drivers}
            pagination={false}
            scroll={{ y: 240 }}
            columns={[
              { title: "Email", dataIndex: "email" },
              {
                title: "Badge IDs",
                dataIndex: "badge_ids",
                render: (badgeIds: string[]) =>
                  badgeIds.length ? badgeIds.join(", ") : "-",
              },
            ]}
          />
        )}
      </Space>
    </Modal>
  );
};
